import FileDownloader from "@/components/FileDownloader/FileDownloader";
import useTranslation from "@/hooks/useTranslation";
import {
  SourceGroupBy,
  useFacilityImpactPerFacilityExport,
  useFacilityImpactPerSourceExport,
} from "@/pages/dashboard/hooks/useFacilityImpact";
import { ImpactMetric } from "@/pages/dashboard/utils/types";
import { DownloadOutlined } from "@ant-design/icons";
import { Segmented, Tabs } from "antd";
import { useState } from "react";
import Widget from "../../../components/Widget/Widget";
import FacilityImpactPerFacility from "./FacilityImpactPerFacility";
import FacilityImpactPerSource from "./FacilityImpactPerSource";

export type FacilityImpactDetailMode = "Absolute" | "Relative (%)";
export type TabKey = "facility" | "sources";

export default function FacilityImpactDetail({ metric }: { metric: ImpactMetric }) {
  const [key, setKey] = useState<TabKey>("facility");
  const translation = useTranslation();
  const [groupBy, setGroupBy] = useState<SourceGroupBy>("name");

  const exportMutationPerFacility = useFacilityImpactPerFacilityExport({ metric: metric.key });
  const exportMutationPerSource = useFacilityImpactPerSourceExport({ groupBy });

  return (
    <Widget
      title={"Facility impact side-by-side"}
      tooltip={translation["dashboard"]["facility"]["side_by_side"]}
      topRight={
        <>
          {key === "facility" && (
            <FileDownloader
              fileUrlFn={() => exportMutationPerFacility.mutateAsync()}
              buttonText={<DownloadOutlined />}
              fileOutputName="facility-impact-per-facility.xlsx"
              addTimestampAndCompany={true}
            />
          )}
          {key === "sources" && (
            <>
              <Segmented
                value={groupBy}
                onChange={setGroupBy}
                options={[
                  { label: "Unique", value: "id" },
                  { label: "Grouped", value: "name" },
                ]}
              />
              <FileDownloader
                fileUrlFn={() => exportMutationPerSource.mutateAsync()}
                buttonText={<DownloadOutlined />}
                fileOutputName="facility-impact-per-source.xlsx"
                addTimestampAndCompany={true}
              />
            </>
          )}
        </>
      }
    >
      <Tabs
        onChange={(key) => setKey(key as TabKey)}
        activeKey={key}
        items={[
          {
            key: "facility",
            label: "Facility",
            children: (
              <FacilityImpactPerFacility
                mode={"Absolute"}
                metric={metric}
              />
            ),
          },
          {
            key: "sources",
            label: "Resource type",
            children: (
              <FacilityImpactPerSource
                groupBy={groupBy}
                metric={metric}
              />
            ),
          },
        ]}
      />
    </Widget>
  );
}
